
import { Component, Prop, Vue } from 'vue-property-decorator';
import { 
  InsightModel, 
  IInsightData, 
  InsightAction 
} from '@/models/insight';
import InsightCard from './InsightCard.vue';

// TypeScript interfaces for component
interface Question {
  id: string;
  question: string; // Display title
  instruction: string; // Detailed instruction for AI
  priority: 'high' | 'medium' | 'low';
  category?: { name: string; code: string };
  icon?: string;
  createdAt?: Date; // For user questions
}

interface ReportContext {
  measure: any;
  dimension: any;
  filterTimerange: string[];
  reportColumns: any[];
}

interface QuestionState {
  isLoading: boolean;
  isComplete: boolean;
  hasError: boolean;
  result?: any;
  error?: string;
  retryCount?: number;
}

@Component({
  components: {
    InsightCard,
  },
})
export default class QuestionCard extends Vue {
  @Prop({ required: true }) question!: Question;
  @Prop({ required: true }) reportContext!: ReportContext;
  @Prop({ required: true }) questionType!: 'predefined' | 'user_created' | 'recent_question' | 'pinned_question';
  @Prop({ default: false }) autoProcess!: boolean;
  @Prop({ default: false }) showDebugInfo!: boolean;
  @Prop({ default: true }) includeSuggestions!: boolean;
  @Prop({ default: 5 }) suggestionCount!: number;
  
  // Action controls
  @Prop({ default: false }) showPinAction!: boolean;
  @Prop({ default: false }) showUnpinAction!: boolean;
  @Prop({ default: false }) showDeleteAction!: boolean;
  @Prop({ default: false }) showMetadata!: boolean;
  @Prop({ default: null }) loadingAction!: string | null;
  @Prop({ default: null }) originalQuestion!: any; // For metadata and actions

  // Question state management
  public questionState: QuestionState = {
    isLoading: false,
    isComplete: false,
    hasError: false
  };

  // Custom instruction editing
  public editingInstructionMode: string | null = null;
  public customInstruction: string = '';
  public isProcessingCustomInstruction: boolean = false;
  
  // Local custom instruction state tracking
  public hasEditedInstruction: boolean = false;
  public originalInstructionText: string = '';
  public originalQuestionText: string = '';
  public lastEditedInstruction: string = '';  // Store the user's last edit

  // Component lifecycle
  async mounted() {
    // Store original question and instruction for comparison and preservation
    this.originalQuestionText = this.question.question;
    this.originalInstructionText = this.question.instruction;
    
    // Debug: Log what the QuestionCard component receives
    
    if (this.autoProcess) {
      await this.processQuestion();
    }
  }

  // Question processing
  public async processQuestion(): Promise<void> {
    if (this.questionState.isLoading || this.questionState.isComplete) return;

    try {
      this.questionState = { 
        isLoading: true, 
        isComplete: false, 
        hasError: false 
      };

      const requestParams = this.buildRequestParams();
      
      const response = await this.callAPI(requestParams);

      this.questionState = {
        isLoading: false,
        isComplete: true,
        hasError: false,
        result: response.data
      };

      this.$emit('question-processed', { 
        questionId: this.question.id, 
        result: response.data 
      });

    } catch (error: any) {
      this.questionState = {
        isLoading: false,
        isComplete: false,
        hasError: true,
        error: error.message || 'Failed to process question'
      };

      // Try to extract suggestions from error response (if API returns them)
      let errorResult = null;
      if (error.response && error.response.data) {
        errorResult = error.response.data;
      }

      this.$emit('question-failed', { 
        questionId: this.question.id, 
        error: this.questionState.error,
        result: errorResult
      });
    }
  }

  // Build request parameters
  private buildRequestParams(): any {
    // STANDARDIZED PARAMETER HANDLING:
    // All requests now consistently send separate 'question' and 'instruction' fields
    // useCustomInstruction flag indicates when user has edited a predefined instruction
    
    let questionText: string;
    let instructionText: string | undefined;
    let useCustomInstruction: boolean = false;
    
    if (this.questionType === 'user_created') {
      // USER-CREATED QUESTIONS
      if (this.question.instruction && this.question.question !== this.question.instruction) {
        // User provided both question and instruction - send both
        questionText = this.question.question;
        instructionText = this.question.instruction;
      } else {
        // User provided only instruction - use it as the main question
        questionText = this.question.instruction || this.question.question;
      }
    } else if (this.hasEditedInstruction) {
      // EDITED PREDEFINED QUESTIONS
      // Keep original question, use edited instruction, mark as custom
      questionText = this.originalQuestionText || this.question.question;
      instructionText = this.question.instruction;
      useCustomInstruction = true;
    } else {
      // UNMODIFIED PREDEFINED QUESTIONS
      // Send both question and instruction separately, but useCustomInstruction = false
      questionText = this.question.question;
      instructionText = this.question.instruction;
    }
    
    // Build base request parameters using the question text (simplified)
    const baseRequestParams = InsightModel.createIndividualQuestionRequest(
      questionText,
      {
        measure: this.reportContext.measure,
        dimension: this.reportContext.dimension,
        filterTimerange: this.reportContext.filterTimerange,
        reportColumns: this.reportContext.reportColumns,
      }
    );
    
    // Always add instruction and useCustomInstruction flag for consistency
    if (instructionText && instructionText.trim()) {
      baseRequestParams.instruction = instructionText.trim();
    }
    baseRequestParams.useCustomInstruction = useCustomInstruction;

    let finalParams;
    if (this.questionType === 'user_created' || this.hasEditedInstruction) {
      // For user questions or edited predefined questions, use direct question API
      finalParams = {
        ...baseRequestParams,
        is_direct_question: true,
        include_suggestions: this.includeSuggestions,
        suggestion_count: this.suggestionCount
      };
    } else {
      // For unmodified predefined questions, use predefined question API
      finalParams = {
        ...baseRequestParams,
        questionId: this.question.id,
        is_direct_question: false,
        include_suggestions: this.includeSuggestions,
        suggestion_count: this.suggestionCount
      };
    }

    // Log final parameter structure for debugging
    if ((finalParams as any).questionId) {
      // no-op
    }

    return finalParams;
  }

  // API calls
  private async callAPI(requestParams: any): Promise<any> {
    const insightModel = new InsightModel();
    
    // Use direct question API for user-created questions, recent questions, pinned questions, OR edited predefined questions
    if (this.questionType === 'user_created' || this.questionType === 'recent_question' || this.questionType === 'pinned_question' || this.hasEditedInstruction) {
      // For direct questions (user-created, recent, pinned, or edited predefined), use the direct question method
      
      // Ensure we have the is_direct_question flag for edited instructions
      const directRequestParams = {
        ...requestParams,
        is_direct_question: true
      };
      
      const response = await insightModel.askDirectQuestion(directRequestParams);
      
      
      if (!response.success) {
        console.error('❌ Direct question failed:', response);
        throw new Error(response.message || 'Failed to process direct question');
      }
      
      return response;
    } else {
      // For unmodified predefined questions, use the existing InsightModel method
      
      const response = await insightModel.askQuestion(requestParams);
      
      
      if (!response.success) {
        console.error('❌ Predefined question failed:', response);
        throw new Error(response.message || 'Failed to process question');
      }
      
      return response;
    }
  }

  // Question management
  public handleCardClick(): void {
    // Only allow clicking to ask question if it hasn't been processed yet
    if (!this.questionState.result && !this.questionState.error && !this.questionState.isLoading) {
      this.processQuestion();
    }
  }

  public deleteQuestion(): void {
    if (this.questionType === 'user_created') {
      // Emit delete event - let parent handle confirmation dialog
      this.$emit('question-deleted', { questionId: this.question.id, confirmed: false });
    }
  }

  public async retryQuestion(): Promise<void> {
    const maxRetries = 3;
    const currentRetry = this.questionState.retryCount || 0;
    
    if (currentRetry >= maxRetries) {
      console.warn('Max retries reached for question:', this.question.id);
      return;
    }

    // Clear state and add retry count
    this.questionState = { 
      isLoading: false, 
      isComplete: false, 
      hasError: false,
      retryCount: currentRetry + 1
    };

    // Wait before retry (exponential backoff)
    const delay = Math.pow(2, currentRetry) * 1000; // 1s, 2s, 4s
    await new Promise(resolve => setTimeout(resolve, delay));

    await this.processQuestion();
  }

  // Custom instruction editing
  public startEditingInstruction(): void {
    if (this.isProcessingCustomInstruction) return;
    
    this.editingInstructionMode = this.question.id;
    this.customInstruction = this.getCurrentEditingInstruction();
  }

  public cancelEditingInstruction(): void {
    this.editingInstructionMode = null;
    this.customInstruction = '';
  }

  public async askWithCustomInstruction(): Promise<void> {
    if (!this.customInstruction?.trim()) return;

    try {
      this.isProcessingCustomInstruction = true;
      this.editingInstructionMode = null;

      // Clear existing state
      this.questionState = {
        isLoading: true,
        isComplete: false,
        hasError: false,
        result: undefined
      };

      // Mark as edited and store the user's edit
      this.hasEditedInstruction = true;
      this.lastEditedInstruction = this.customInstruction.trim();
      this.question.instruction = this.customInstruction.trim();
      
      // Build request params - this will properly handle question vs customInstruction
      const requestParams = {
        ...this.buildRequestParams(),
        clear_cache: true  // Always clear cache for edited instructions
      };

      const response = await this.callAPI(requestParams);

      this.questionState = {
        isLoading: false,
        isComplete: true,
        hasError: false,
        result: response.data
      };

      this.$emit('question-processed', { 
        questionId: this.question.id, 
        result: response.data 
      });

    } catch (error: any) {
      this.questionState = {
        isLoading: false,
        isComplete: false,
        hasError: true,
        error: error.message || 'Failed to process custom instruction'
      };

      // Try to extract suggestions from error response (if API returns them)
      let errorResult = null;
      if (error.response && error.response.data) {
        errorResult = error.response.data;
      }

      this.$emit('question-failed', { 
        questionId: this.question.id, 
        error: this.questionState.error,
        result: errorResult
      });

    } finally {
      this.isProcessingCustomInstruction = false;
    }
  }

  // Helper methods
  public getCardClass(): string {
    const classes = [];
    
    if (this.questionType === 'user_created') {
      classes.push('question-card--user-created');
    }
    
    if (this.questionState.result || this.questionState.error) {
      classes.push('question-card--processed');
    }
    
    return classes.join(' ');
  }

  public getQuestionColor(): string {
    return this.questionType === 'user_created' ? 'purple' : 'primary';
  }

  public getPriorityColor(): string {
    const colors = {
      'high': 'error',
      'medium': 'warning', 
      'low': 'success'
    };
    return colors[this.question.priority] || 'grey';
  }

  public get hasCustomInstruction(): boolean {
    return this.hasEditedInstruction;
  }

  public getDisplayInstruction(): string {
    // Always show the original instruction to the user, not the modified one
    return this.originalInstructionText || this.question.instruction;
  }

  public getCurrentEditingInstruction(): string {
    // Get the instruction that should be used for editing
    // If the user has edited the instruction, show their last edit
    // Otherwise, show the original instruction for first-time editing
    if (this.hasEditedInstruction && this.lastEditedInstruction) {
      return this.lastEditedInstruction;  // Return the user's last edited instruction
    }
    return this.originalInstructionText || this.question.instruction;
  }

  public getInsightDisplayText(): string {
    const result = this.questionState.result;
    if (!result?.insight) return 'No insight available';

    const insight = result.insight;
    
    // For custom instructions, prioritize detailed data.text
    if (result.is_custom_instruction && insight.data?.text) {
      return insight.data.text.replace(/\\n/g, '\n');
    }
    
    // Check for detailed text in data.text
    if (insight.data?.text && insight.data.text !== insight.insight) {
      return insight.data.text.replace(/\\n/g, '\n');
    }
    
    return insight.insight || 'Analysis completed';
  }

  // Event handlers for InsightCard
  public handleInsightAction(action: InsightAction, insight: IInsightData): void {
    this.$emit('insight-action', action, insight);
  }

  public handleApplyFilter(filterParams: any): void {
    this.$emit('apply-filter', filterParams);
  }

  public handleNavigate(url: string): void {
    this.$emit('navigate', url);
  }

  public handleExportData(exportParams: any): void {
    this.$emit('export-data', exportParams);
  }

  // Download event handlers
  public handleDownloadSuccess(event: any): void {
    // Show success notification
    (this as any).$toast.success(
      `Downloaded ${event.filename} (${event.rowCount} rows)`,
      { 
        duration: 4000,
        position: 'bottom-right'
      }
    );
    
    // Emit event for parent components
    this.$emit('download-success', {
      questionId: this.question.id,
      question: this.question.question,
      ...event
    });
  }

  public handleDownloadError(error: any): void {
    // Show error notification
    const errorMessage = error?.message || 'Failed to download CSV file';
    (this as any).$toast.error(
      `Download failed: ${errorMessage}`,
      { 
        duration: 6000,
        position: 'bottom-right'
      }
    );
    
    // Log error for debugging
    console.error('Download error:', error);
    
    // Emit event for parent components
    this.$emit('download-error', {
      questionId: this.question.id,
      question: this.question.question,
      error: errorMessage
    });
  }

  // Enhanced action methods
  public handlePinAction(): void {
    if (this.loadingAction) { return; }

    const questionId = this.question.id;

    // Set loading state
    this.$emit('pin-action-started', { questionId });

    if (this.isPinned()) {
      this.$emit('question-unpinned', { questionId, question: this.question });
    } else {
      this.$emit('question-pinned', { questionId, question: this.question });
    }
  }

  public handleUnpinAction(): void {
    if (this.loadingAction) { return; }

    const questionId = this.question.id;
    
    // Emit unpin event with original question for context
    this.$emit('question-unpinned', { 
      questionId,
      originalQuestion: this.originalQuestion
    });
  }

  public handleDeleteAction(): void {
    if (this.loadingAction) { return; }

    const questionId = this.question.id;

    // Emit delete event - let parent handle confirmation dialog
    this.$emit('question-deleted', { questionId, confirmed: false });
  }

  public isPinned(): boolean {
    // Check if this question is pinned from original question model
    if (this.originalQuestion && typeof this.originalQuestion.isPinned === 'function') {
      return this.originalQuestion.isPinned();
    }
    return false;
  }

  public getCategoryColor(): string {
    // Extract measure code for category color mapping
    const measureCode = this.reportContext.measure?.code || '';

    const colorMap: Record<string, string> = {
      sales: 'success',
      inventory: 'warning',
      customer: 'info',
      product: 'primary',
      financial: 'secondary',
      operations: 'accent',
      order: 'indigo',
      marketing: 'purple',
      supplier: 'teal',
    };

    for (const [key, color] of Object.entries(colorMap)) {
      if (measureCode.toLowerCase().includes(key)) {
        return color;
      }
    }

    return 'grey';
  }

  public getCategoryName(): string {
    const measureCode = this.reportContext.measure?.code || this.question.category?.code || '';

    if (!measureCode) {
      return 'General';
    }

    // Capitalize first letter and replace hyphens/underscores with spaces
    return measureCode
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  }

  public getLastExecuted(): string {
    // Get last executed info from original question model
    if (this.originalQuestion && typeof this.originalQuestion.getFormattedLastExecuted === 'function') {
      const formatted = this.originalQuestion.getFormattedLastExecuted();
      return formatted !== 'Never' ? formatted : '';
    }
    return '';
  }

  // Enhanced card class method
  public getEnhancedCardClass(): string {
    const classes = ['question-card'];
    
    // Add base classes from original getCardClass
    if (this.questionType === 'user_created') {
      classes.push('question-card--user-created');
    }
    
    if (this.questionState.result || this.questionState.error) {
      classes.push('question-card--processed');
    }
    
    // Add enhanced classes
    if (this.showMetadata || this.showPinAction || this.showUnpinAction || this.showDeleteAction) {
      classes.push('question-card--enhanced');
    }
    
    if (this.loadingAction) {
      classes.push('question-card--loading');
    }

    // Add question type classes
    if (this.questionType === 'recent_question') {
      classes.push('question-card--recent');
    }
    
    if (this.questionType === 'pinned_question') {
      classes.push('question-card--pinned');
    }
    
    return classes.join(' ');
  }

  // Loading message method
  public getLoadingMessage(): string {
    const messages: Record<string, string> = {
      pinning: 'Pinning question...',
      unpinning: 'Unpinning question...',
      deleting: 'Deleting question...',
    };

    return messages[this.loadingAction || ''] || 'Processing...';
  }
}
